// Dashboard.tsx - with Launch Announcement Modal (popup)
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { motion, AnimatePresence } from 'framer-motion';
import { ref, onValue, off } from 'firebase/database';
import { auth, db } from '../firebase';
import {
  ChevronRight,
  Instagram,
  Youtube,
  BookOpen,
  X,
} from 'lucide-react';

// Custom hooks
import { useAuth } from '../hooks/useAuth';
import { useOptimizedQuery } from '../hooks/useOptimizedQuery';

// Components
import LoadingScreen from '../components/dashboard/LoadingScreen';
import Header from '../components/dashboard/Header';
import HeroSlider from '../components/dashboard/HeroSlider';
import StatsGrid from '../components/dashboard/StatsGrid';
import MatchesSection from '../components/dashboard/MatchesSection';
import GamesSection from '../components/dashboard/GamesSection';
import MobileMenu from '../components/dashboard/Mobilemenu';
import AnnouncementModal from '../components/dashboard/AnnouncementModal';
import BottomNav from '../components/dashboard/BottomNav';

// Types
interface UserProfile {
  name: string;
  userName: string;
  balance: number;
  winnings: number;
  matchPlayed: number;
  finishes: number;
  defaultTier: string;
  AccountStatus: boolean;
}

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [greeting, setGreeting] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showAnnouncements, setShowAnnouncements] = useState(false);
  const [showLaunchModal, setShowLaunchModal] = useState(false);

  // Use optimized queries for static data
  const { data: appDetails } = useOptimizedQuery<any>('AppDetails', { cacheDuration: 10 * 60 * 1000 });
  const { data: announcementsData } = useOptimizedQuery<any>('Announcements', { cacheDuration: 15 * 60 * 1000 });
  
  // Prepare slides from cached data
  const heroSlides = useMemo(() => {
    if (!appDetails) return [];
    return [
      { image: appDetails.image1 || '', title: appDetails.title1 || '', link: appDetails.link1 || '' },
      { image: appDetails.image2 || '', title: appDetails.title2 || '', link: appDetails.link2 || '' },
      { image: appDetails.image3 || '', title: appDetails.title3 || '', link: appDetails.link3 || '' },
    ].filter(s => s.image && s.title);
  }, [appDetails]);
  
  // Transform announcements
  const announcements = useMemo(() => {
    if (!announcementsData) return [];
    return Object.entries(announcementsData).map(([id, value]: [string, any]) => ({
      id,
      title: value.title || '',
      message: value.message || '',
      date: value.date || '',
      type: value.type || 'info'
    }));
  }, [announcementsData]);

  const gameBanners = useMemo(() => ({
    bgmiBanner: appDetails?.bgmiBanner || 'https://images.unsplash.com/photo-1542751371-adc38448a05e?w=800&h=400&fit=crop',
    ffBanner: appDetails?.ffBanner || 'https://images.unsplash.com/photo-1542751110-97427bbecf20?w=800&h=400&fit=crop'
  }), [appDetails]);

  // Set greeting based on time
  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting('Good Morning');
    else if (hour < 17) setGreeting('Good Afternoon');
    else setGreeting('Good Evening');
  }, []);

  // Check localStorage for launch modal – show only once per day
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    const storedDate = localStorage.getItem('launchModalDate');
    if (storedDate !== today) {
      // Show modal and store today's date
      setShowLaunchModal(true);
      localStorage.setItem('launchModalDate', today);
    } else {
      setShowLaunchModal(false);
    }
  }, []);

  // Only use realtime for user profile (critical data)
  useEffect(() => {
    if (!user) {
      if (!authLoading) {
        navigate('/login', { replace: true });
      }
      return;
    }

    const userId = user.uid;
    const userRef = ref(db, `Accounts/${userId}`);
    
    const handleProfileUpdate = (snapshot: any) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        setProfile({
          name: data.name || '',
          userName: data.userName || '',
          balance: typeof data.balance === 'number' ? data.balance : 0,
          winnings: typeof data.winnings === 'number' ? data.winnings : 0,
          matchPlayed: typeof data.matchPlayed === 'number' ? data.matchPlayed : 0,
          finishes: typeof data.finishes === 'number' ? data.finishes : 0,
          defaultTier: data.defaultTier || 'T3',
          AccountStatus: data.AccountStatus === true,
        });
      } else {
        console.warn('No profile found for user:', userId);
      }
      setProfileLoading(false);
    };

    const handleError = (error: any) => {
      console.error('Profile listener error:', {
        code: error.code,
        message: error.message,
        userId: userId
      });
      setProfileLoading(false);
      if (error.code === 'PERMISSION_DENIED') {
        navigate('/login', { replace: true });
      }
    };

    const unsubscribe = onValue(
      userRef,
      handleProfileUpdate,
      handleError
    );
    
    return () => {
      unsubscribe();
    };
  }, [user, navigate, authLoading]);

  // Suspended account handling
  useEffect(() => {
    if (profile?.AccountStatus !== false) {
      return;
    }
    navigate('/account-suspended', {
      replace: true,
    });
    void signOut(auth).catch((error) => {
      if (process.env.NODE_ENV === 'development') {
        console.error('Logout after suspension failed:', error);
      }
    });
  }, [profile?.AccountStatus, navigate]);

  const handleLogout = useCallback(async () => {
    await signOut(auth);
    navigate('/');
  }, [navigate]);

  const handleHeroClick = useCallback(
    (link: string) => {
      if (!link) return;
      if (link.startsWith('http://') || link.startsWith('https://')) {
        const allowedDomains = [
          'youtube.com',
          'youtu.be',
          'instagram.com',
          'facebook.com',
          'twitter.com',
          'discord.com',
          'discord.gg',
          'twitch.tv',
        ];
        try {
          const url = new URL(link);
          const hostname = url.hostname.toLowerCase();
          const isAllowed = allowedDomains.some(domain => 
            hostname === domain || hostname.endsWith(`.${domain}`)
          );
          if (!isAllowed) {
            console.warn('Blocked external link:', link);
            return;
          }
          window.open(link, '_blank', 'noopener,noreferrer');
          return;
        } catch (error) {
          console.warn('Invalid URL:', link);
          return;
        }
      }
      const allowedInternalPaths = [
        '/game/',
        '/tournament/',
        '/matches/',
        '/wallet',
        '/profile',
        '/main/gameScreen',
      ];
      const isInternalAllowed = allowedInternalPaths.some(
        (prefix) => link === prefix || link.startsWith(prefix)
      );
      if (!isInternalAllowed) {
        console.warn('Blocked invalid internal link:', link);
        return;
      }
      navigate(link);
    },
    [navigate]
  );

  const handleGameSelect = useCallback((game: string) => {
    navigate(`/game/${game}`);
  }, [navigate]);

  // Memoize stats
  const stats = useMemo(() => ({
    totalMatches: profile?.matchPlayed || 0,
    finishes: profile?.finishes || 0,
    winnings: profile?.winnings || 0,
    defaultTier: profile?.defaultTier || 'T3'
  }), [profile]);

  if (authLoading || profileLoading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#0f0f1a] to-[#0a0a0f]">
      {/* Simplified background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
      </div>

      <Header
        profile={profile}
        onMenuClick={() => setIsMobileMenuOpen(true)}
        onNotificationClick={() => setShowAnnouncements(true)}
        announcementsCount={announcements.length}
      />

      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        onLogout={handleLogout}
      />

      <AnnouncementModal
        isOpen={showAnnouncements}
        onClose={() => setShowAnnouncements(false)}
        announcements={announcements}
      />

      <div className="pt-20 pb-24 px-4 md:px-8 max-w-7xl mx-auto">
        {/* Welcome Section */}
        <div className="mb-6">
          <p className="text-gray-400 text-sm">{greeting},</p>
          <h1 className="text-2xl md:text-3xl font-rajdhani font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
            {profile?.name?.split(' ')[0] || 'Player'}!
          </h1>
        </div>

        <HeroSlider slides={heroSlides} onSlideClick={handleHeroClick} />
        <StatsGrid stats={stats} />

        <MatchesSection userId={user?.uid || ''} />
        <GamesSection banners={gameBanners} onGameSelect={handleGameSelect} />

        {/* Rules Button */}
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          onClick={() => navigate('/rules')}
          className="group relative overflow-hidden bg-gradient-to-r from-purple-500/20 to-blue-500/20 rounded-2xl p-5 border border-purple-500/30 hover:border-purple-500/50 transition-all duration-300 mb-6 w-full"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-purple-500/0 to-blue-500/0 group-hover:from-purple-500/10 group-hover:to-blue-500/10 transition-all duration-500" />
          <div className="flex items-center gap-4 relative z-10">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center group-hover:scale-110 transition-transform">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div className="text-left flex-1">
              <h3 className="text-white font-rajdhani font-bold text-lg">Rules & Information</h3>
              <p className="text-gray-400 text-sm">Learn about tiers, points, and tournament rules</p>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-white group-hover:translate-x-1 transition-all" />
          </div>
        </motion.button>

        {/* SOCIAL MEDIA SECTION */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          {/* Instagram Button */}
          <a
            href="https://instagram.com/the.arenax"
            target="_blank"
            rel="noopener noreferrer"
            className="group relative overflow-hidden bg-gradient-to-r from-pink-500/10 to-purple-500/10 rounded-2xl p-5 border border-pink-500/30 hover:border-pink-500/50 transition-all duration-300"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-pink-500/0 to-purple-500/0 group-hover:from-pink-500/10 group-hover:to-purple-500/10 transition-all duration-500" />
            <div className="flex items-center gap-4 relative z-10">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-pink-500 to-purple-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Instagram className="w-6 h-6 text-white" strokeWidth={2.2} />
              </div>
              <div className="text-left">
                <h3 className="text-white font-rajdhani font-bold text-lg">Instagram</h3>
                <p className="text-gray-400 text-sm">Follow for updates</p>
              </div>
            </div>
            <ChevronRight className="absolute right-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-hover:text-white group-hover:translate-x-1 transition-all" />
          </a>

          {/* YouTube Button */}
          <a
            href="https://youtube.com/@lastzoneindia"
            target="_blank"
            rel="noopener noreferrer"
            className="group relative overflow-hidden bg-gradient-to-r from-red-500/10 to-orange-500/10 rounded-2xl p-5 border border-red-500/30 hover:border-red-500/50 transition-all duration-300"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-red-500/0 to-orange-500/0 group-hover:from-red-500/10 group-hover:to-orange-500/10 transition-all duration-500" />
            <div className="flex items-center gap-4 relative z-10">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-red-500 to-orange-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.376.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.376-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </div>
              <div className="text-left">
                <h3 className="text-white font-rajdhani font-bold text-lg">YouTube</h3>
                <p className="text-gray-400 text-sm">Watch highlights</p>
              </div>
            </div>
            <ChevronRight className="absolute right-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-hover:text-white group-hover:translate-x-1 transition-all" />
          </a>
        </div>
      </div>

      <BottomNav />

      {/* LAUNCH ANNOUNCEMENT MODAL - POPUP */}
      <AnimatePresence>
        {showLaunchModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowLaunchModal(false)} // click outside to close
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              transition={{ type: 'spring', damping: 20 }}
              className="relative bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl max-w-lg w-full border border-purple-500/40 shadow-2xl shadow-purple-500/20 p-6 md:p-8"
              onClick={(e) => e.stopPropagation()} // prevent closing when clicking inside
            >
              {/* Close button */}
              <button
                onClick={() => setShowLaunchModal(false)}
                className="absolute top-3 right-3 text-gray-400 hover:text-white transition p-1 rounded-full hover:bg-white/10"
              >
                <X className="w-6 h-6" />
              </button>

              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-purple-500/30">
                  <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                  </svg>
                </div>
                <h3 className="text-white font-rajdhani font-bold text-2xl md:text-3xl mb-2">
                  🚀 ArenaX Launching Soon!
                </h3>
                <p className="text-gray-300 text-base md:text-lg mb-4">
                  ArenaX is opening on <strong className="text-purple-400">10th September</strong> and all matches will start from <strong className="text-purple-400">10th September</strong>.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a
                    href="https://youtube.com/@lastzoneindia"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-xl font-rajdhani font-bold text-base transition-all shadow-lg shadow-red-600/25 hover:shadow-red-600/40"
                  >
                    <Youtube className="w-6 h-6" />
                    Subscribe on YouTube
                  </a>
                  <span className="text-gray-400 text-sm">to get updated & watch live gameplay</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Dashboard;